import {
  CENTRED,
  lecternAssembly,
  lecternPose,
  panelPlacement,
  slideClear,
  subjectOf,
  type Assembly,
  type Bounds,
  type FramingScene,
  type LecternPose,
  type PanelPlacement,
  type Step,
  type Vec3,
} from '@vrlearning/lesson-schema';
import {
  facesInPlace,
  resolveView,
  rigPose,
  type ResolvedView,
  type SceneGeometry,
} from './view.js';

/** What placement needs of the stage (render/stage.ts), without WebGL. */
export type PlacementStage = {
  readonly eyeHeight: number;
  readonly rig: {
    readonly position: { readonly x: number; readonly y: number; readonly z: number };
    readonly rotation: { readonly y: number };
  };
  travelTo(arrive: () => void, instant?: boolean): void;
  placeRig(view: ResolvedView): void;
};

/** What placement needs of the lectern (render/ui.ts). */
export type PlacementUi = {
  /** Puts the lectern at `pose` in the rig: at once, or sliding there. */
  placeLectern(pose: LecternPose, options: { instant?: boolean }): void;
  /** Where the lectern is now, which is not where it was sent while it slides. */
  currentLectern?(): LecternPose | undefined;
};

export type PlacementDeps = {
  readonly stage: PlacementStage;
  readonly ui: PlacementUi;
  readonly geometry: SceneGeometry;
  /**
   * The lesson's steps, in order: where a pupil stands to face a world's
   * feature comes from the steps before it, not from the way the class came.
   * Without them, from where the pupil stands now.
   */
  readonly steps?: readonly Step[];
  /** The step the pupil is on now: the class may move on while a fade runs. */
  readonly currentStep: () => Step;
  /**
   * Where a step's lectern stands whatever framing would say: the furnish step's side lectern
   * (ADR-037, 45° to the left). Undefined for every other step.
   */
  readonly fixed?: (step: Step) => PanelPlacement | undefined;
  /** Told after every placement of the lectern, for the preview's aids and step counter. */
  readonly onPlaced?: (
    lectern: PanelPlacement,
    subject: Bounds | undefined,
    eye: number,
    assembly: Assembly,
  ) => void;
};

/**
 * Where the pupil stands and where the VR lectern goes, step by step: frame
 * first, then slide aside (docs/superpowers/specs/2026-09-28-clear-view-design.md).
 *
 * - A step with a view is placed at the black of its fade, for the eyes'
 *   height then, and its lectern with it.
 * - A step without one leaves the pupil where they are; the lectern slides
 *   only if it must stand elsewhere: the step is about something else, shows
 *   other buttons, or a model has come into its way. A slide that would pass
 *   through a model is a jump instead.
 * - The lectern never goes through a model, and stands high enough that
 *   every button of the step is seen (lesson-schema's framing.ts and lectern.ts).
 * - Every new step's lectern starts centred, so a swing never outlives what it
 *   was for. Only a re-placing of the same subject -- the eyes' height
 *   arriving -- starts from where it is, which is where hysteresis counts.
 * - Answers and hotspots are not step changes, and move nothing.
 */
export class StepPlacement {
  readonly #deps: PlacementDeps;
  #lectern: PanelPlacement = CENTRED;
  /** Where the lectern stands in the rig; undefined until first placed. */
  #pose: LecternPose | undefined;
  #placedFor: string | undefined;
  #started = false;

  constructor(deps: PlacementDeps) {
    this.#deps = deps;
  }

  /** Where the lectern stands now. */
  get lectern(): PanelPlacement {
    return this.#lectern;
  }

  /**
   * A step began: the first of the lesson, or another. `atArrival` runs at the
   * black of the fade when there is one, else straight away: a scene that
   * snaps to a step (render/motion.ts) does it unseen.
   *
   * `travel` fades even when the step has no view (ADR-036): a new 360° photo
   * always goes in through black. The pupil stays where they stand.
   */
  stepChanged(step: Step, atArrival?: () => void, options: { travel?: boolean } = {}): void {
    const first = !this.#started;
    this.#started = true;
    if (this.#resolve(step) || options.travel === true) {
      this.#deps.stage.travelTo(() => {
        atArrival?.();
        this.#arrive(step);
      }, first);
      return;
    }
    atArrival?.();
    this.#placeLectern(this.#deps.currentStep(), first, CENTRED);
  }

  /**
   * The headset has just learned the eyes' height; everything so far was
   * placed for 1.6 m. Placed again at once, without a fade.
   */
  eyeKnown(): void {
    const step = this.#deps.currentStep();
    const view = this.#resolve(step);
    if (view) this.#deps.stage.placeRig(view);
    const same = this.#key(step) === this.#placedFor;
    this.#placeLectern(step, true, same ? this.#lectern : CENTRED);
  }

  /** At the black: the rig for the step that asked for the fade, the lectern for the step the pupil is on now. */
  #arrive(viewStep: Step): void {
    const view = this.#resolve(viewStep);
    if (view) this.#deps.stage.placeRig(view);
    this.#placeLectern(this.#deps.currentStep(), true, CENTRED);
  }

  #resolve(step: Step): ResolvedView | undefined {
    if (!step.view) return undefined;
    const { rig } = this.#deps.stage;
    const eye = this.#deps.stage.eyeHeight;
    return resolveView(step.view, this.#deps.geometry, {
      focus: step.focus,
      eye,
      // A world's feature is faced from where the steps before it put the
      // pupil (ADR-032): the same after a jump or going back as going on.
      from: this.#standBefore(step, eye) ?? [rig.position.x, rig.position.y + eye, rig.position.z],
      scene: this.#scene(step),
      questions: step,
    });
  }

  /**
   * Where the last step before `step` that places the pupil puts them, for
   * eyes at `eye`; the origin when none does. Steps that only face a feature
   * place no one and are passed over, as lesson-schema's assessFraming does.
   */
  #standBefore(step: Step, eye: number): Vec3 | undefined {
    const steps = this.#deps.steps;
    const index = steps?.findIndex((candidate) => candidate.id === step.id) ?? -1;
    if (!steps || index < 0) return undefined;
    for (let j = index - 1; j >= 0; j--) {
      const earlier = steps[j]!;
      const view = earlier.view;
      if (!view) continue;
      // A feature or a big model is faced from where the pupil stands, which places no one.
      if (facesInPlace(view, this.#scene(earlier))) continue;
      const resolved = resolveView(view, this.#deps.geometry, {
        focus: earlier.focus,
        eye,
        scene: this.#scene(earlier),
        questions: earlier,
      });
      if (!resolved) continue;
      const [x, y, z] = rigPose(resolved).position;
      return [x, y + eye, z];
    }
    return [0, eye, 0];
  }

  /** The scene as the step ends: framing uses the result, so it can be seen. */
  #scene(step: Step): FramingScene {
    const geometry = this.#deps.geometry;
    return geometry.framingFor?.(step) ?? geometry.framing;
  }

  #key(step: Step): string | undefined {
    return subjectOf(step, this.#scene(step))?.ids.join('+');
  }

  /**
   * From where the pupil stands now: slides there, or jumps under a fade --
   * or when the way there passes through a model.
   */
  #placeLectern(step: Step, instant: boolean, from: PanelPlacement): void {
    const { stage, ui } = this.#deps;
    const eye = stage.eyeHeight;
    const scene = this.#scene(step);
    const subject = subjectOf(step, scene);
    const assembly = lecternAssembly(step);
    const obstacles = [...(scene.obstacles ?? scene.bounds).values()];
    const rig = stage.rig;
    const at = {
      eye: [rig.position.x, rig.position.y + eye, rig.position.z] as const,
      yaw: rig.rotation.y,
      eyeHeight: eye,
    };
    this.#lectern =
      this.#deps.fixed?.(step) ??
      panelPlacement({
        ...at,
        subject: subject?.bounds,
        current: from,
        obstacles,
        assembly,
      });
    this.#placedFor = subject?.ids.join('+');
    const pose = lecternPose(eye, this.#lectern.side, this.#lectern.degrees, {
      distance: this.#lectern.distance,
      assembly,
    });
    const previous = this.#pose;
    this.#pose = pose;
    // A slide starts from where the lectern is -- perhaps still on its way
    // somewhere -- not from where it was last sent.
    const now = ui.currentLectern?.() ?? previous;
    const jump =
      instant || !now || !slideClear({ rig: at, from: now, to: pose, assembly, obstacles });
    // Where it already is: nothing to do, and nothing to see.
    if (jump || !samePose(previous, pose)) ui.placeLectern(pose, { instant: jump });
    this.#deps.onPlaced?.(this.#lectern, subject?.bounds, eye, assembly);
  }
}

function samePose(a: LecternPose | undefined, b: LecternPose): boolean {
  return (
    a !== undefined &&
    a.yaw === b.yaw &&
    a.tilt === b.tilt &&
    a.position.every((value, i) => value === b.position[i])
  );
}
